// PATH: src/pages/ObjectiveTrialDetailPage.tsx
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import DoneAllIcon from "@mui/icons-material/DoneAllRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useObjectiveTrialsApi } from "../api";
import type { ObjectiveTrialChapter, ObjectiveTrialDetail } from "../models/domain";
import { PageHeader, StatusPill, errorMessage, useConfirm, useToast } from "../components/ui";
import { SentInstructions } from "../components/settings/InstructionChain";
import { embedRolePrompts, versionsUsed } from "../components/settings/rolePrompts";
import { useEmbedPrompts } from "../components/settings/useEmbedPrompts";
import { TRIAL_POLL_MS, formatUsd, originLabel } from "../components/trials/trialLogic";
import {
  applyMessage,
  canDiscardObjectiveTrial,
  chapterCounts,
  isActive,
  objectiveTrialStatus,
  staleLabel,
} from "../components/objectiveTrials/objectiveTrialLogic";

const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—";

/** Un chapitre : le brouillon leçon par leçon face aux objectifs servis, et ce qui serait perdu. */
const ChapterView: React.FC<{ ch: ObjectiveTrialChapter; check: boolean }> = ({ ch, check }) => {
  const n = chapterCounts(ch);
  const lessonTitle = new Map((ch.summary?.items ?? []).map((l) => [l.lecon_ref, l.titre]));
  return (
    <Card sx={{ mt: 2 }}>
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h5" dir="auto">
          {ch.title || ch.document_id}
        </Typography>
        {ch.error ? (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {ch.error}
          </Alert>
        ) : (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <StatusPill tone="success">{n.reused} repris</StatusPill>
              <StatusPill tone="info">{n.created} nouveaux</StatusPill>
              <StatusPill tone={n.orphaned ? "error" : "neutral"}>{n.orphaned} orphelins</StatusPill>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                {ch.served} objectifs servis aujourd'hui · {ch.model}
                {ch.summary ? ` · ${ch.summary.lessons} leçons, ${ch.summary.retried} nouvelle(s) tentative(s), ${ch.summary.review_merged} fusion(s) à la relecture` : ""}
              </Typography>
            </Stack>
            {check && ch.stale && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Ce chapitre ne sera pas appliqué : {staleLabel(ch.stale)}.
              </Alert>
            )}
          </>
        )}
      </Box>
      {!ch.error && (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Leçon</TableCell>
                <TableCell>Objectif de l'essai</TableCell>
                <TableCell>Identité</TableCell>
                <TableCell>Objectif servi aujourd'hui</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ch.objectives.map((o) => (
                <TableRow key={o.ordinal}>
                  <TableCell sx={{ minWidth: 140 }} dir="auto">
                    {(o.lecon_ref && lessonTitle.get(o.lecon_ref)) || o.lecon_ref || "—"}
                  </TableCell>
                  <TableCell dir="auto">{o.text}</TableCell>
                  <TableCell>
                    {o.status === "reused" ? (
                      <StatusPill tone="success" title="Même identifiant que l'objectif servi : la progression des élèves est gardée">
                        repris
                      </StatusPill>
                    ) : (
                      <StatusPill tone="info" title="Nouvel objectif : aucune progression à reprendre">
                        nouveau
                      </StatusPill>
                    )}
                  </TableCell>
                  <TableCell dir="auto" sx={{ color: "text.secondary" }}>
                    {o.status === "reused" ? (o.served_text === o.text ? "identique" : o.served_text) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {ch.orphans.map((o) => (
                <TableRow key={o.id} sx={{ bgcolor: "action.hover" }}>
                  <TableCell dir="auto">{(o.lecon_ref && lessonTitle.get(o.lecon_ref)) || o.lecon_ref || "—"}</TableCell>
                  <TableCell sx={{ color: "text.disabled", fontStyle: "italic" }}>aucun objectif de l'essai</TableCell>
                  <TableCell>
                    <StatusPill tone="error" title="Objectif servi sans correspondant : la progression des élèves serait perdue">
                      orphelin
                    </StatusPill>
                  </TableCell>
                  <TableCell dir="auto">{o.text}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
};

/**
 * Détail d'un essai d'objectifs : où il en est, ce qu'il a coûté, et chapitre
 * par chapitre le brouillon face aux objectifs servis — repris (même
 * identifiant, progression gardée), nouveaux, orphelins (progression perdue).
 * Puis « Appliquer » (écriture d'un import, faits ré-extraits) ou « Jeter ».
 */
const ObjectiveTrialDetailPage: React.FC = () => {
  const { trialId = "" } = useParams<{ trialId: string }>();
  const navigate = useNavigate();
  const { getObjectiveTrial, applyObjectiveTrial, discardObjectiveTrial } = useObjectiveTrialsApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [detail, setDetail] = React.useState<ObjectiveTrialDetail | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const basePrompts = useEmbedPrompts();

  const load = React.useCallback(async () => {
    try {
      const { data } = await getObjectiveTrial(trialId);
      setDetail(data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger l'essai."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialId]);
  React.useEffect(() => {
    void load();
  }, [load]);
  const status = detail?.trial.status;
  React.useEffect(() => {
    if (!status || !isActive(status)) return;
    const id = window.setInterval(() => void load(), TRIAL_POLL_MS);
    return () => window.clearInterval(id);
  }, [status, load]);

  const apply = async () => {
    if (!detail) return;
    const ok = await confirm({
      title: "Appliquer l'essai ?",
      message: applyMessage(detail.chapters, detail.apply_facts_usd),
      confirmLabel: "Appliquer",
      danger: detail.chapters.some((c) => !c.stale && c.orphans.length > 0),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await applyObjectiveTrial(trialId);
      toast.success("Application lancée : objectifs, puis faits.");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible d'appliquer l'essai."));
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    const ok = await confirm({
      title: "Jeter l'essai ?",
      message: "Le brouillon est supprimé ; les objectifs servis ne changent pas. Le coût déjà payé reste affiché.",
      confirmLabel: "Jeter",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await discardObjectiveTrial(trialId);
      toast.success("Essai jeté.");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de jeter l'essai."));
    } finally {
      setBusy(false);
    }
  };

  const trial = detail?.trial;
  const s = trial ? objectiveTrialStatus(trial.status) : null;
  const r = trial?.resolved.objective_extraction;
  const check = trial?.status === "done";

  return (
    <Box sx={{ maxWidth: 1280 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/objective-trials")} sx={{ mb: 1 }}>
        Essais d'objectifs
      </Button>
      <PageHeader
        title="Essai d'objectifs"
        subtitle="Le brouillon de l'essai face aux objectifs servis : repris (progression gardée), nouveaux, orphelins (progression perdue)."
        actions={
          trial && (
            <Stack direction="row" spacing={1}>
              {canDiscardObjectiveTrial(trial.status) && (
                <Button variant="outlined" color="error" startIcon={<DeleteIcon />} disabled={busy} onClick={() => void discard()}>
                  Jeter
                </Button>
              )}
              {trial.status === "done" && (
                <Button
                  variant="contained"
                  startIcon={<DoneAllIcon />}
                  disabled={busy || detail?.apply_objectives === 0}
                  onClick={() => void apply()}
                >
                  Appliquer
                </Button>
              )}
            </Stack>
          )
        }
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!detail || !trial || !s ? (
        !error && <Skeleton height={160} />
      ) : (
        <>
          <Card>
            <Box sx={{ p: { xs: 2, md: 2.5 }, display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" } }}>
              <Box>
                <Typography variant="subtitle2">Statut</Typography>
                <StatusPill tone={s.tone}>{s.label}</StatusPill>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  Lancé le {when(trial.created_at)} par {trial.created_by}
                  {trial.applied_at ? ` · appliqué le ${when(trial.applied_at)}` : ""}
                </Typography>
                {isActive(trial.status) && <LinearProgress sx={{ mt: 1 }} />}
                {trial.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {trial.error}
                  </Alert>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle2">Réglage de l'essai</Typography>
                <Typography variant="body2">
                  <span className="mono">{r?.model ?? "—"}</span>{" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    ({originLabel(r?.origin.model)})
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {r?.instructions ? `Consigne : ${originLabel(r.origin.instructions)}, ci-dessous` : "Aucune consigne"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Coût</Typography>
                <Typography variant="body2">
                  Estimé {formatUsd(trial.estimate_usd)} · réel {formatUsd(detail.cost?.usd ?? trial.actual_usd)}
                  {detail.cost ? ` (${detail.cost.calls} appels)` : ""}
                </Typography>
                {check && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Appliquer écrirait {detail.apply_objectives} objectifs et ré-extrairait leurs faits :{" "}
                    {detail.apply_facts_usd === null ? "coût non chiffrable" : `≈ ${formatUsd(detail.apply_facts_usd)}`}.
                  </Typography>
                )}
              </Box>
            </Box>
          </Card>

          <Card sx={{ mt: 2 }}>
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Typography variant="h5">Ce que l'agent des objectifs a reçu</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Dans l'ordre d'envoi : le prompt de base, puis la consigne en fin de message.
              </Typography>
              <SentInstructions
                prompts={embedRolePrompts("objective_extraction", basePrompts.prompts, basePrompts.error)}
                used={versionsUsed(detail.chapters.map((c) => c.summary?.prompt_version))}
                instructions={r?.instructions}
                origin={r?.origin.instructions}
              />
            </Box>
          </Card>

          {trial.apply_report && (
            <Card sx={{ mt: 2 }}>
              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography variant="h5">Application</Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {trial.apply_report.map((a) => (
                    <Typography key={a.document_id} variant="body2" dir="auto">
                      <b>{a.title}</b> :{" "}
                      {a.applied
                        ? `${a.objectives} objectifs écrits (${a.reused} repris, ${a.orphaned} orphelins), ${a.units} unités de faits extraites` +
                          (a.orphaned_overrides || a.orphaned_reports
                            ? ` — travail éditorial emporté : ${a.orphaned_overrides} décision(s), ${a.orphaned_reports} signalement(s)`
                            : "")
                        : a.stale
                          ? `non appliqué, ${staleLabel(a.stale)}`
                          : `non appliqué (${a.error})`}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Card>
          )}

          {detail.chapters.map((ch) => (
            <ChapterView key={ch.document_id} ch={ch} check={check} />
          ))}
        </>
      )}
    </Box>
  );
};

export default ObjectiveTrialDetailPage;
